import {RestDocument, RestWebServiceDocument} from "@softvision/webpdf-wsclient-9-typescript";
import DocumentSettings from "./DocumentSettings";
import MimeType from "./mimetype/MimeType";
import MimeTypes from "./mimetype/MimeTypes";
import Exception from "../../shared/exception/Exception";
import Container from "./Container";
import ContainerFactory from "./ContainerFactory";
import ViewerType from "./DataType";
import {pdfjs} from "react-pdf";
import ErrorCode from "../../shared/exception/ErrorCode";
import {
	Annotation,
	AnnotationsDocument,
	Attachment,
	AttachmentsDocument,
	Description,
	Document as MetadataDocument,
	DocumentFile,
	Field,
	Folder,
	FoldersDocument,
	FormDocument,
	HistoryEntry,
	MetadataFormsFormat,
	MetadataInformation,
	MetadataPassword,
	MetadataPdfa,
	MetadataSignature,
	MetadataViewerPreferences,
	PagePages,
	PagesDocument,
	WebserviceException,
	SignaturesDocument
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../PortalContext";
import Translate from "../../shared/translation/Translate";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import FileTypeGroupFilter from "./mimetype/FileTypeGroupFilter";
import FileTypeGroup from "./mimetype/FileTypeGroup";
import MimeTypeFilter from "./mimetype/MimeTypeFilter";

enum OperationToolboxAttachmentAttachmentEnum {
	GLOBAL = "global",
	PAGE = "page"
}

export interface PortalDocumentInterface {
	id?: string;
	name: string;
	extension: string;
	fileName: string;
	size: number;
	archiveType: string;
	attachments: number;
	portfolio: boolean;
	hasPasswordProtection: boolean;
	hasSignatureProtection: boolean;
	signatures: number;
	updated: number;
	settings: DocumentSettings;
	attachmentSettings: Array<DocumentSettings>;
}

export default class PortalDocument extends RestWebServiceDocument implements RestDocument {
	private context: PortalContext;
	private containerMap: Map<number, Container>;
	private attachmentContainerMap: Map<string, Map<number, Container>>;
	private updated: number;
	private executionError?: Exception;

	constructor(context: PortalContext, id?: string) {
		super(id);

		this.context = context;
		this.updated = Date.now();
		this.containerMap = new Map<number, Container>();
		this.attachmentContainerMap = new Map<string, Map<number, Container>>();
	}

	public getFileTypeGroup(): string {
		return FunctionHelper.applyValue(
			this.getDocumentFile()?.fileTypeGroups, DocumentFile.getFileTypeGroupsDefault()
		);
	}

	public detectMimeType(data: string): MimeType {
		if (data.startsWith("%PDF-")) {
			return MimeTypes.PDF;
		}

		return MimeTypes.UNKNOWN;
	}

	public getMimeType(attachment?: Attachment): MimeType {
		let mimeType: MimeType;

		if (FunctionHelper.exists(attachment)) {
			mimeType = new MimeType(FunctionHelper.applyValue(attachment?.mimeType, MimeTypes.UNKNOWN.toString()));
		} else {
			mimeType = MimeTypes.fromId(
				FunctionHelper.applyValue(this.getDocumentFile()?.fileTypeId, 0)
			);

			if (mimeType === MimeTypes.UNKNOWN) {
				mimeType = MimeTypes.fromString(
					FunctionHelper.applyValue(this.getDocumentFile()?.mimeType, MimeTypes.UNKNOWN.toString())
				);
			}
		}

		return mimeType;
	}

	public get container(): Container {
		let activeHistoryElement: HistoryEntry | undefined;

		try {
			activeHistoryElement = this.activeHistory();
		} catch (e: any) {
			// can be ignored here
		}

		let container: Container | undefined = this.containerMap.get(
			FunctionHelper.applyValue(activeHistoryElement?.id, 1)
		);

		if (!FunctionHelper.exists(container)) {
			let viewerType: ViewerType = ContainerFactory.getViewerType(this.getMimeType(), this.getFileTypeGroup());
			container = ContainerFactory.createInstance(viewerType, this);

			if (this.containerMap.size > this.context.getPage().state.documentCache) {
				let tempKey: number = this.containerMap.keys().next().value;
				this.containerMap.get(tempKey)?.clean();
				this.containerMap.delete(tempKey);
			}

			this.containerMap.set(FunctionHelper.applyValue(activeHistoryElement?.id, 1), container);
		}

		return container!;
	}

	public getAttachmentContainer(attachment: Attachment): Container {
		let attachmentKey: string = FunctionHelper.applyValue(attachment?.objectKey, Attachment.getObjectKeyDefault());
		let activeHistoryElement: HistoryEntry = this.activeHistory();
		let attachmentMap: Map<number, Container>;

		if (!this.attachmentContainerMap.has(attachmentKey)) {
			this.attachmentContainerMap.set(attachmentKey, new Map<number, Container>());
		}

		attachmentMap = this.attachmentContainerMap.get(attachmentKey)!;
		let container: Container | undefined;

		if (attachmentMap.has(activeHistoryElement.id)) {
			container = attachmentMap.get(activeHistoryElement.id!);
		}

		if (!FunctionHelper.exists(container)) {
			let viewerType: ViewerType = ContainerFactory.getViewerType(new MimeType(
				FunctionHelper.applyValue(attachment?.mimeType, Attachment.getMimeTypeDefault())
			));
			container = ContainerFactory.createInstance(viewerType);

			if (this.attachmentContainerMap.size > this.context.getPage().state.documentCache) {
				let tempKey: number = attachmentMap.keys().next().value;
				attachmentMap.get(tempKey)?.clean();
				attachmentMap.delete(tempKey);
			}

			attachmentMap.set(activeHistoryElement.id!, container);
		}

		return container!;
	}

	public set data(data: any) {
		let container: Container = this.container;
		container.clean();

		// TODO: detect pdf with text mimetype -> to be discussed
		// if (MimeTypeFilter.checkSelection([this.getMimeType()], [MimeTypes.TEXT])) {
		// 	let binaryString: string = String.fromCharCode(...new Uint8Array(data));
		// 	let mimeType: MimeType = this.detectMimeType(binaryString);
		// 	let viewerType: ViewerType = ContainerFactory.getViewerType(mimeType);
		// 	container = ContainerFactory.createInstance(viewerType, this);
		//
		// 	let activeHistoryElement: HistoryEntry | undefined;
		//
		// 	try {
		// 		activeHistoryElement = this.activeHistory();
		// 	} catch (e: any) {
		// 		// can be ignored here
		// 	}
		//
		// 	this.containerMap.set(FunctionHelper.applyValue(activeHistoryElement?.id, 1), container);
		// }

		container.data = data;
	}

	public get data(): any {
		return this.container.data;
	}

	public setAttachmentData(attachment: Attachment, data: any) {
		let container: Container = this.getAttachmentContainer(attachment);
		container.clean();

		if (MimeTypeFilter.checkSelection(
			[MimeTypes.fromString(
				FunctionHelper.applyValue(attachment.mimeType, Attachment.getMimeTypeDefault())
			)],
			[MimeTypes.TEXT]
		)) {
			let binaryString: string = String.fromCharCode(...new Uint8Array(data));
			let mimeType: MimeType = this.detectMimeType(binaryString);
			let viewerType: ViewerType = ContainerFactory.getViewerType(mimeType);
			container = ContainerFactory.createInstance(viewerType, this);

			let attachmentKey: string = FunctionHelper.applyValue(
				attachment?.objectKey, Attachment.getObjectKeyDefault()
			);
			let attachmentMap: Map<number, Container> = this.attachmentContainerMap.get(attachmentKey)!;

			let activeHistoryElement: HistoryEntry = this.activeHistory();
			attachmentMap.set(activeHistoryElement.id!, container);
		}

		container.data = data;
	}

	public getAttachmentData(attachment: Attachment): any {
		return this.getAttachmentContainer(attachment).data;
	}

	/**
	 * @throws {Exception}
	 */
	public async loadViewerData(attachment?: Attachment): Promise<any> {
		let container: Container;

		if (FunctionHelper.exists(attachment)) {
			await this.loadRawAttachmentData(attachment!);
			container = this.getAttachmentContainer(attachment!);
		} else {
			container = this.container;
		}

		if (!FunctionHelper.exists(container.data)) {
			await this.context.documentManager.downloadRawData(this.getSourceDocumentId()!);
			return await this.loadViewerData(attachment);
		}

		return await container.loadViewerData(this, attachment);
	}

	public cleanViewerData(): void {
		this.container.clean();
		this.setUpdated();
	}

	/**
	 * @throws {Exception}
	 */
	public async loadRawAttachmentData(attachment: Attachment): Promise<void> {
		if (this.getMimeType() !== MimeTypes.PDF) {
			throw new Exception(Translate.value("errors.files.load", {ns: "message"}));
		}

		if (FunctionHelper.exists(this.getAttachmentData(attachment))) {
			return this.getAttachmentData(attachment);
		}

		let pdfDocument: pdfjs.PDFDocumentProxy = await this.loadViewerData();
		let attachmentContent: BlobPart | undefined;

		if (FunctionHelper.exists(attachment.page) && attachment.page !== 0) {
			let pageProxy: pdfjs.PDFPageProxy = await pdfDocument.getPage(attachment.page!);
			let annotations: Array<any> = await pageProxy.getAnnotations();

			for (let annotation of annotations) {
				if (annotation.subtype === 'FileAttachment' && annotation.file.filename === attachment.name) {
					attachmentContent = annotation.file.content;
					break;
				}
			}
		} else {
			let globalAttachments: any = await pdfDocument.getAttachments();

			for (let key in globalAttachments) {
				if (globalAttachments.hasOwnProperty(key) && globalAttachments[key].filename === attachment.name) {
					attachmentContent = globalAttachments[key].content;
					break;
				}
			}
		}

		if (!FunctionHelper.exists(attachmentContent)) {
			throw new Exception(Translate.value("errors.files.load", {ns: "message"}));
		}

		let fileBlob: Blob = new Blob([attachmentContent!], {type: attachment.mimeType});
		this.setAttachmentData(attachment, await fileBlob.arrayBuffer());
	}

	public getViewerSettings(): DocumentSettings {
		let settings: DocumentSettings = this.getSettings();

		if (this.isPortfolioFile()) {
			let selectedAttachment: Attachment | undefined = this.getAttachment(
				FunctionHelper.applyValue(settings.portfolioSelection, "")
			);
			settings = this.getSettings(selectedAttachment);
		}

		return settings;
	}

	public getSettings(attachment?: Attachment): DocumentSettings {
		let container: Container;

		if (FunctionHelper.exists(attachment)) {
			container = this.getAttachmentContainer(attachment!);
		} else {
			container = this.container;
		}

		return container.settings;
	}

	public async getNumberOfPages(attachment?: Attachment): Promise<number> {
		let numberOfPages: number = 1;
		let data: any;
		let container: Container;

		try {
			if (FunctionHelper.exists(attachment)) {
				data = await this.loadViewerData(attachment!);
				container = this.getAttachmentContainer(attachment!);
			} else {
				data = await this.loadViewerData();
				container = this.container;
			}

			numberOfPages = await container.getNumberOfPages(data);
		} catch (e: any) {
			// can be ignored here
		}

		return numberOfPages;
	}

	public hasHtmlPreview(): boolean {
		return FileTypeGroupFilter.checkSelection(
				[this.getFileTypeGroup()], [FileTypeGroup.MAIL]
			) ||
			MimeTypeFilter.checkSelection(
				[this.getMimeType()], [
					MimeTypes.DOC, MimeTypes.DOCX, MimeTypes.VCARD, MimeTypes.VCAL
				]
			);
	}

	public isArchive(): boolean {
		if (this.getMimeType() !== MimeTypes.PDF) {
			return false
		}

		return FunctionHelper.applyValue(this.metadata?.information?.pdfa?.part, MetadataPdfa.getPartDefault()) !== "";
	}

	public isPortfolio(): boolean {
		return FunctionHelper.applyValue(
			this.metadata?.information?.portfolio, MetadataInformation.getPortfolioDefault()
		);
	}

	public isPortfolioFile(): boolean {
		let documentSettings: DocumentSettings = this.getSettings();

		let folderSelected: boolean = FunctionHelper.exists(this.getFolderByPath(
			FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
		));

		return this.isPortfolio() && !FunctionHelper.isEmpty(documentSettings.portfolioSelection) && !folderSelected
	}

	public hasSignature(): boolean {
		return this.signatureAnnotations.length > 0;
	}

	public isEditable(options?: {
		checkPages?: boolean,
		checkPassword?: boolean,
		checkCertificate?: boolean,
		checkDynXFA?: boolean,
		checkPortfolio?: boolean,
		checkArchive?: boolean
	}): boolean {
		if (this.getMimeType() !== MimeTypes.PDF) {
			return true;
		}

		if ((!FunctionHelper.exists(options?.checkPages) || options?.checkPages) &&
			FunctionHelper.applyValue(this.metadata?.information?.pages, MetadataInformation.getPagesDefault()) === 0
		) {
			return false;
		}

		if ((!FunctionHelper.exists(options?.checkPassword) || options?.checkPassword) &&
			this.hasPasswordProtection("open") && (!this.getDocumentFile()?.password?.isOpenSet || this.hasError())
		) {
			return false;
		}

		if ((!FunctionHelper.exists(options?.checkCertificate) || options?.checkCertificate) &&
			this.hasCertificateProtection() && (!this.getDocumentFile()?.password?.isCertificateSet || this.hasError())
		) {
			return false;
		}

		if ((!FunctionHelper.exists(options?.checkDynXFA) || options?.checkDynXFA) &&
			FunctionHelper.applyValue(
				this.metadata?.information?.formType, MetadataInformation.getFormTypeDefault()
			) === MetadataFormsFormat.DynamicXFA
		) {
			return false;
		}

		if ((!FunctionHelper.exists(options?.checkPortfolio) || options?.checkPortfolio) && this.isPortfolio()
		) {
			return false;
		}

		if ((!FunctionHelper.exists(options?.checkArchive) || options?.checkArchive) &&
			this.isArchive() && !this.container.settings.editArchiveConfirmation
		) {
			return false;
		}

		return true;
	}

	public isViewable(): boolean {
		if (this.getMimeType() === MimeTypes.PDF) {
			let formType: MetadataFormsFormat | undefined = FunctionHelper.applyValue(
				this.metadata?.information?.formType, MetadataInformation.getFormTypeDefault()
			);
			if (formType === MetadataFormsFormat.DynamicXFA) {
				return false;
			}

			if (!FunctionHelper.exists(this.metadata?.pages)) {
				return false;
			}

			if (this.hasPasswordProtection("open") && (!this.getDocumentFile()?.password?.isOpenSet || this.hasError())) {
				return false;
			}

			if (this.hasCertificateProtection() && (!this.getDocumentFile()?.password?.isCertificateSet || this.hasError())) {
				return false;
			}
		}

		if (this.hasError() && this.getError()?.code !== ErrorCode.PDF_PASSWORD_OWNER) {
			return false;
		}

		return true;
	}

	public hasPasswordProtection(type?: "open" | "permission"): boolean {
		let documentInfo: DocumentFile | undefined = this.getDocumentFile();

		if (this.getMimeType() !== MimeTypes.PDF) {
			return false
		}

		let passwordErrors: Array<ErrorCode> = [
			ErrorCode.PDF_PASSWORD_MISSING,
			ErrorCode.PDF_PASSWORD_USER
		];

		if (FunctionHelper.exists(documentInfo)) {
			if (passwordErrors.indexOf(FunctionHelper.applyValue(
				documentInfo?.error?.errorCode, WebserviceException.getErrorCodeDefault()
			)) !== -1) {
				return true;
			}

			if (type === "open") {
				return FunctionHelper.applyValue(
					this.metadata?.security?.password?.hasOpenPassword, MetadataPassword.getHasOpenPasswordDefault()
				);
			}

			if (type === "permission") {
				return FunctionHelper.applyValue(
					this.metadata?.security?.password?.hasPermissionPassword,
					MetadataPassword.getHasPermissionPasswordDefault()
				);
			}

			return (FunctionHelper.applyValue(
					this.metadata?.security?.password?.hasOpenPassword, MetadataPassword.getHasOpenPasswordDefault()
				)) ||
				(FunctionHelper.applyValue(
					this.metadata?.security?.password?.hasPermissionPassword,
					MetadataPassword.getHasPermissionPasswordDefault()
				));
		}

		return false;
	}

	public hasCertificateProtection(): boolean {
		let documentInfo: DocumentFile | undefined = this.getDocumentFile();

		let certificateErrors: Array<ErrorCode> = [
			ErrorCode.PDF_CERTIFICATE_KEYPAIR_FAILURE,
			ErrorCode.PDF_CERTIFICATE_PRIVATE_KEY_FAILURE,
			ErrorCode.PDF_CERTIFICATE_CERTIFICATE_FAILURE,
			ErrorCode.PDF_CERTIFICATE_KEYPAIR_MISSING,
			ErrorCode.PDF_CERTIFICATE_RECIPIENT_NOT_MATCHING
		];

		if (this.getMimeType() !== MimeTypes.PDF) {
			return false
		}

		if (FunctionHelper.exists(documentInfo)) {
			if (certificateErrors.indexOf(FunctionHelper.applyValue(
				documentInfo?.error?.errorCode, WebserviceException.getErrorCodeDefault()
			)) !== -1) {
				return true;
			}

			return FunctionHelper.applyValue(
				this.metadata?.security?.password?.hasAsymmetricEncryption,
				MetadataPassword.getHasAsymmetricEncryptionDefault()
			);
		}

		return false;
	}

	public getFolderById(key: number): Folder | undefined {
		let folders: Array<Folder> = FunctionHelper.applyValue(
			this.metadata?.folders?.folder, FoldersDocument.getFolderDefault()
		);

		for (let folder of folders) {
			if (folder.id === key) {
				return folder;
			}
		}

		return;
	}

	public getFolderByPath(path: string): Folder | undefined {
		let folders: Array<Folder> = FunctionHelper.applyValue(
			this.metadata?.folders?.folder, FoldersDocument.getFolderDefault()
		);

		for (let folder of folders) {
			if (folder.path === path) {
				return folder;
			}
		}

		return;
	}

	public getPage(pagenumber: number): PagePages | undefined {
		let pages: Array<PagePages> = FunctionHelper.applyValue(
			this.metadata?.pages?.page, PagesDocument.getPageDefault()
		);

		for (let page of pages) {
			if (page.number === pagenumber) {
				return page;
			}
		}
	}

	public getAnnotation(key: string, includeReplies?: boolean): Array<Annotation> {
		let result: Array<Annotation> = [];
		let annotations: Array<Annotation> = FunctionHelper.applyValue(
			this.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		);

		for (let annotation of annotations) {
			if (annotation.objectKey === key) {
				result.push(annotation);
			}

			if (includeReplies && annotation.markup?.relation?.comment?.objectID === key) {
				result.push(annotation);
			}
		}

		return result;
	}

	public getAttachment(key: string): Attachment | undefined {
		let attachments: Array<Attachment> = FunctionHelper.applyValue(
			this.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);

		for (let attachment of attachments) {
			if (attachment.objectKey === key) {
				return attachment;
			}
		}
	}

	public getAttachments(type?: OperationToolboxAttachmentAttachmentEnum): Array<Attachment> {
		let result: Array<Attachment> = [];
		let attachments: Array<Attachment> = FunctionHelper.applyValue(
			this.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);

		for (let attachment of attachments) {
			if ((type === OperationToolboxAttachmentAttachmentEnum.GLOBAL && attachment.page !== 0) ||
				(type === OperationToolboxAttachmentAttachmentEnum.PAGE && attachment.page === 0)) {
				continue;
			}

			result.push(attachment);
		}

		return result;
	}

	public getFileName(): string {
		return this.getDocumentFile()?.fileName + "." + this.getDocumentFile()?.fileExtension;
	}

	public getDocumentTitle(): string {
		let documentTitle: string = FunctionHelper.applyValue(
			this.metadata?.information?.description?.title, Description.getTitleDefault()
		);

		if (FunctionHelper.applyValue(
			this?.metadata?.information?.viewerPreferences?.displayDocTitle,
			MetadataViewerPreferences.getDisplayDocTitleDefault()
		) && documentTitle.trim() !== "") {
			return documentTitle;
		}

		return this.getFileName();
	}

	public get archiveType(): string {
		return FunctionHelper.applyValue(
				this.metadata?.information?.pdfa?.part, MetadataPdfa.getPartDefault()
			) +
			FunctionHelper.applyValue(
				this.metadata?.information?.pdfa?.conformance, MetadataPdfa.getConformanceDefault()
			);
	}

	public getSignatureField(objectKey?: string | null): Field | undefined {
		if (!FunctionHelper.exists(objectKey)) {
			return;
		}

		for (let field of this.signatureFields) {
			if (field.signature?.objectKey === objectKey) {
				return field;
			}
		}
	}

	public get signatureFields(): Array<Field> {
		let formFields: Array<Field> = FunctionHelper.applyValue(
			this.metadata?.form?.field, FormDocument.getFieldDefault()
		);

		let fields: Array<Field> = [];
		for (let field of formFields) {
			if (field.type === "Sig") {
				fields.push(field);
			}
		}

		return fields;
	}

	public get signatureAnnotations(): Array<Annotation> {
		let annotations: Array<Annotation> = FunctionHelper.applyValue(
			this.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		);

		let signatureAnnotations: Array<Annotation> = [];
		for (let annotation of annotations) {
			if (!FunctionHelper.exists(annotation.field)) {
				continue;
			}

			signatureAnnotations.push(annotation);
		}

		return signatureAnnotations;
	}

	public get signatures(): Array<MetadataSignature> {
		return FunctionHelper.applyValue(
			this.metadata?.signatures?.signature, SignaturesDocument.getSignatureDefault()
		);
	}

	public get metadata(): MetadataDocument | undefined {
		return this.getDocumentFile()?.metadata;
	}

	public getError(): Exception | undefined {
		let documentError: WebserviceException | undefined = this.getDocumentFile()?.error;

		if (FunctionHelper.exists(documentError) && this.hasError()) {
			return Exception.parseExceptionBean(documentError!);
		}
	}

	public hasError(): boolean {
		let documentError: WebserviceException | undefined = this.getDocumentFile()?.error;

		if (!FunctionHelper.exists(documentError)) {
			return false;
		}

		return documentError!.errorCode !== 0;
	}

	public setUpdated(): void {
		this.updated = Date.now();
	}

	public getViewerType(attachment?: Attachment): ViewerType {
		if (FunctionHelper.exists(attachment)) {
			return this.getAttachmentContainer(attachment!).getType();
		}

		return this.container.getType();
	}

	public setExecutionError(value?: Exception): void {
		this.executionError = value;
		this.setUpdated();
	}

	public getExecutionError(): Exception | undefined {
		return this.executionError;
	}

	public unlock(): void {
		this.setUpdated();
		this.getSettings().locked = false;
	}

	public lock(): void {
		this.setUpdated();
		this.getSettings().locked = true;
	}

	public isLocked(): boolean {
		return this.getSettings().locked;
	}

	public toJson(): PortalDocumentInterface {
		let attachmentSettings: Array<DocumentSettings> = [];

		for (let attachment of this.getAttachments()) {
			attachmentSettings.push(this.getSettings(attachment).toJson());
		}

		return {
			id: this.getSourceDocumentId(),
			name: FunctionHelper.applyValue(this.getDocumentFile()?.fileName, DocumentFile.getFileNameDefault()),
			extension: FunctionHelper.applyValue(
				this.getDocumentFile()?.fileExtension, DocumentFile.getFileExtensionDefault()
			),
			fileName: this.getFileName(),
			size: FunctionHelper.applyValue(this.getDocumentFile()?.fileSize, DocumentFile.getFileSizeDefault()),
			updated: this.updated,
			archiveType: this.archiveType,
			attachments: this.getAttachments().length,
			portfolio: this.isPortfolio(),
			hasPasswordProtection: this.hasPasswordProtection(),
			hasSignatureProtection: this.hasCertificateProtection(),
			signatures: this.signatureAnnotations.length,
			settings: this.getSettings().toJson(),
			attachmentSettings: attachmentSettings
		};
	}
}